import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Bot,
  ExternalLink,
  FlaskConical,
  MessageSquare,
  Award,
  Clock,
  Radio,
} from "lucide-react";
import { formatChannelName } from "../thread/threadUtils";
import { useUserDirectory, normalizeAgentType } from "../../hooks/useUserDirectory";

interface BatchConversationsTabProps {
  threads: any[];
  comments?: any[];
  evaluations?: any[];
}

export function BatchConversationsTab({
  threads,
  comments = [],
  evaluations = [],
}: BatchConversationsTabProps) {
  const { getAgentDisplayName, sanitizeProtocoloName } = useUserDirectory();

  const getAgentBadge = (agentType: string) => {
    const norm = normalizeAgentType(agentType);
    const customName = getAgentDisplayName(norm);
    switch (norm) {
      case "sales":
        return {
          label: customName || "Quotes",
          role: "Sales Concierge",
          badgeClass:
            "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
        };
      case "biblioteca":
        return {
          label: customName || "Analytics",
          role: "Analytics Lead",
          badgeClass:
            "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800",
        };
      case "frontdesk":
        return {
          label: customName || "Sofia",
          role: "Front Desk Concierge",
          badgeClass:
            "bg-amber-50 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800",
        };
      case "feedback":
        return {
          label: customName || "Feedback",
          role: "Feedback & Curation",
          badgeClass:
            "bg-cyan-50 dark:bg-cyan-950/60 text-cyan-800 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800",
        };
      case "email_drafting":
        return {
          label: customName || "Email Drafter",
          role: "Gmail Inbound Concierge",
          badgeClass:
            "bg-violet-50 dark:bg-violet-950/60 text-violet-800 dark:text-violet-300 border-violet-200 dark:border-violet-800",
        };
      default:
        return {
          label: customName || norm || "Agent",
          role: "Agent",
          badgeClass:
            "bg-stone-100 dark:bg-stone-800 text-stone-800 dark:text-stone-300 border-stone-200 dark:border-stone-700",
        };
    }
  };

  const getStatusPill = (status: string) => {
    switch (status) {
      case "active":
        return "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 ring-1 ring-emerald-500/20";
      case "completed":
        return "bg-sky-50 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800";
      case "simulation":
        return "bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800";
      case "archived":
        return "bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 border-stone-200 dark:border-stone-700";
      default:
        return "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border-stone-200 dark:border-stone-700";
    }
  };

  const renderScorePill = (score?: number | null, isFlagged?: boolean) => {
    if (isFlagged) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
          <AlertTriangle className="w-3 h-3 text-rose-600" />
          <span>{score != null ? score.toFixed(2) : "Flagged"}</span>
        </span>
      );
    }

    if (score == null) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 border border-stone-200 dark:border-stone-700">
          Unrated
        </span>
      );
    }

    if (score >= 4.0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
          <span>{score.toFixed(2)}</span>
        </span>
      );
    }

    if (score >= 3.0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
          <span>{score.toFixed(2)}</span>
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
        <span>{score.toFixed(2)}</span>
      </span>
    );
  };

  if (!threads || threads.length === 0) {
    return (
      <div className="p-8 text-center bg-stone-50 dark:bg-stone-900/50 rounded-xl border border-stone-200 dark:border-stone-800">
        <Bot className="w-8 h-8 text-stone-400 mx-auto mb-2" />
        <h4 className="text-xs font-semibold text-stone-700 dark:text-stone-300 mb-1">
          No Linked Conversations
        </h4>
        <p className="text-[11px] text-stone-500 max-w-sm mx-auto">
          This batch does not currently have any conversation threads associated with its comments or evaluations.
        </p>
      </div>
    );
  }

  const sortedThreads = useMemo(() => {
    return [...threads].sort(
      (a, b) =>
        new Date(b.last_message_at || b.created_at || 0).getTime() -
        new Date(a.last_message_at || a.created_at || 0).getTime()
    );
  }, [threads]);

  return (
    <div className="space-y-3">
      {sortedThreads.map((thread) => {
        const agent = getAgentBadge(thread.agent_type);
        const isSim = thread.status === "simulation" || Boolean(thread.payload_metadata?.is_simulation);
        const threadComments = comments.filter((c) => c.thread_id === thread.id);
        const threadEvaluations = evaluations.filter((e) => e.thread_id === thread.id);
        const preview = thread.last_message_preview || thread.metadata?.last_message || "Turn exchange recorded";
        const threadTitle =
          thread.title ||
          thread.metadata?.title ||
          thread.payload_metadata?.title ||
          threadComments.find((c: any) => c.thread_title)?.thread_title ||
          threadEvaluations.find((e: any) => e.thread_title)?.thread_title;

        return (
          <div
            key={thread.id}
            className="p-4 rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 hover:border-stone-300 dark:hover:border-stone-700 transition shadow-2xs space-y-3"
          >
            {/* Header row */}
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2.5 min-w-0">
                <div
                  className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                    isSim
                      ? "bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300"
                      : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"
                  }`}
                >
                  {isSim ? <FlaskConical className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                </div>

                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${agent.badgeClass}`}
                  >
                    {getAgentDisplayName(thread.agent_type, thread.agent_name || agent.label)}
                  </span>
                  <Link
                    to={`/threads/${thread.id}`}
                    className="font-semibold text-stone-900 dark:text-stone-100 hover:text-emerald-700 dark:hover:text-emerald-400 transition truncate max-w-xs text-xs"
                    title={sanitizeProtocoloName(threadTitle) || thread.id}
                  >
                    {sanitizeProtocoloName(threadTitle) || `#${thread.id.slice(0, 8)}`}
                  </Link>
                  {threadTitle && (
                    <span className="font-mono text-[10px] text-stone-400 dark:text-stone-500 truncate" title={thread.id}>
                      #{thread.id.slice(0, 8)}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border uppercase tracking-wider ${getStatusPill(thread.status)}`}>
                  {thread.status}
                </span>

                {renderScorePill(thread.aggregate_score, thread.is_flagged)}

                <Link
                  to={`/threads/${thread.id}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 border border-emerald-200 dark:border-emerald-800 transition"
                  title="Inspect conversation thread"
                >
                  <span>Inspect</span>
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            </div>

            {/* Turn preview */}
            <div className="px-3 py-2 rounded-lg bg-stone-50 dark:bg-stone-800/60 border border-stone-100 dark:border-stone-800 text-xs text-stone-700 dark:text-stone-300 italic font-sans leading-relaxed">
              "{preview}"
            </div>

            {/* Footer metrics and associations */}
            <div className="flex flex-wrap items-center justify-between gap-3 text-[11px] text-stone-500 dark:text-stone-400 pt-1 border-t border-stone-100 dark:border-stone-800/80">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1">
                  <MessageSquare className="w-3 h-3 text-stone-400" />
                  <span>{thread.message_count ?? 1} turns</span>
                </span>

                {threadComments.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 text-[10px] font-medium">
                    <MessageSquare className="w-2.5 h-2.5" />
                    <span>{threadComments.length} comment{threadComments.length > 1 ? "s" : ""} in batch</span>
                  </span>
                )}

                {threadEvaluations.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 text-[10px] font-medium">
                    <Award className="w-2.5 h-2.5" />
                    <span>{threadEvaluations.length} evaluation{threadEvaluations.length > 1 ? "s" : ""} in batch</span>
                  </span>
                )}

                {thread.source_channel_id && (
                  <span className="inline-flex items-center gap-1 font-mono text-[10px] text-stone-400" title={`Channel ID: ${thread.source_channel_id}`}>
                    <Radio className="w-2.5 h-2.5" />
                    <span>{formatChannelName(thread.source_channel_id, thread.metadata)}</span>
                  </span>
                )}
              </div>

              {(thread.last_message_at || thread.created_at) && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] text-stone-400"
                  title={thread.created_at ? `Created: ${new Date(thread.created_at).toLocaleDateString()}` : undefined}
                >
                  <Clock className="w-2.5 h-2.5" />
                  <span>{new Date(thread.last_message_at || thread.created_at).toLocaleDateString()}</span>
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
